import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { pickSponsorSelection } from '@/scheduling/lib/pick-sponsor-selection';
import { Skeleton } from '@/components/ui/skeleton';
import { Segmented, type SegmentedOption } from '@scripsteam/scrips-signal-ds';
import { ShieldCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCalculatePrice } from '@/services/scheduling/use-calculate-price';
import { usePostBookCalculatePrice } from '@/services/scheduling/use-postbook-calculate-price';
import { CoPaymentWarningBanner } from './co-payment-warning-banner';
import { addedServicesToIndividualServices } from '@/scheduling/lib/individual-services';
import { SPONSOR_LABELS } from '@/scheduling/lib/sponsor-labels';

const SPONSOR_TYPE_OPTIONS: SegmentedOption<string>[] = [
  { value: 'Self-Pay', label: 'Self-pay' },
  { value: 'Insurance', label: 'Insurance' },
  { value: 'Corporate', label: 'Corporate' },
];
import { EMPTY_GUID } from '@/shared/lib/empty-guid';
import type {
  HealthInsurance,
  CorporateAgreement,
} from '@/services/scheduling/use-prebook-selected-patient';
import type { AppointmentFormValues } from '@/types/appointment-form';
import type { AppointmentInvoiceModel } from '@/types/scheduling-api';

/**
 * Payment / Sponsor section — Flutter `payment_widget.dart` +
 * `insurance_child_widget.dart` + `corporate_child_widget.dart`.
 *
 * Layout (Flutter parity):
 *   - SPONSOR header
 *   - Three icon tabs: Self-pay / Insurance / Corporate
 *   - For Insurance / Corporate: an "INSURANCE" / "CORPORATE" sub-header
 *     with an Edit link, a horizontal scrollable carousel of the patient's
 *     pre-loaded sponsors, and a detail card showing description +
 *     Exp Date for the picked sponsor.
 *   - Always: a breakdown card with five rows — Gross Total / Discount
 *     Total / Net Total / Patient Payable / Sponsor Amount Total.
 *
 * Selection updates `sponsorId` + `policyNo` + full `sponsor` payer
 * object → `useCalculatePrice` (queryKey'd) re-fires once → breakdown
 * updates from `invoiceTotals`.
 */

interface PaymentSectionProps {
  organizationId: string;
  practiceId: string;
  currency?: string;
  /** Open the Insurance / Corporate edit screens (Flutter "Edit" link). */
  onEditInsurance?: () => void;
  onEditCorporate?: () => void;
  /** Open the edit screens from the empty state ("Add" button). */
  onAddInsurance?: () => void;
  onAddCorporate?: () => void;
  /**
   * The appointment's persisted invoice (edit mode). When present, the TOTAL
   * block mirrors these server totals instead of the PreBook estimate — Flutter
   * `payment_widget.dart` reads `appointment.invoice.{grossTotal,…}` and
   * refreshes them from every PreLoadAppointmentDetails (line 307). PreBook
   * (`PreBookCalculatePrice` over the form's `addedServices`) is a *create-flow*
   * estimator; in edit mode that service list is decoupled from the invoice, so
   * using it showed stale totals after a billing-tab service edit (PROD-1809).
   */
  editInvoice?: AppointmentInvoiceModel | null;
}

function payerName(payer: { payerName?: string; name?: string } | undefined): string {
  return payer?.payerName ?? payer?.name ?? '';
}

function placeholderInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();
}

function isExpiredIso(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const t = Date.parse(iso);
  return Number.isFinite(t) && t < Date.now();
}

function formatExpDate(iso: string | null | undefined): string {
  if (!iso) return 'N/A';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return 'N/A';
  // Flutter `scDateFormat` → e.g. "Dec 31, 2026"
  return new Date(t).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

interface SponsorChipProps {
  imageUrl?: string;
  name: string;
  isSelected: boolean;
  isVerified?: boolean;
  isExpired?: boolean;
  onClick: () => void;
}

/** Mirrors Flutter `insurance_child_widget.dart:139-247` — 24×24 logo,
 *  optional verified shield, name underneath; selected gets a brand
 *  border on the logo + brand-color name. */
function SponsorChip({
  imageUrl,
  name,
  isSelected,
  isVerified,
  isExpired: expired,
  onClick,
}: SponsorChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={name}
      className="flex flex-col items-center w-[64px] flex-shrink-0 group"
    >
      <div
        className={cn(
          'w-8 h-8 rounded overflow-hidden bg-grey-100 flex items-center justify-center border-2',
          isSelected ? 'border-brand-primary' : 'border-transparent',
        )}
      >
        {imageUrl ? (
          <img src={imageUrl} alt={name} className="w-full h-full object-cover" />
        ) : (
          <span className="text-xs font-bold text-text-secondary">
            {placeholderInitials(name) || '—'}
          </span>
        )}
      </div>
      <div className="mt-1 flex items-center gap-1 max-w-full">
        {isVerified ? (
          <ShieldCheck className="h-3 w-3 text-status-success flex-shrink-0" />
        ) : null}
        <span
          className={cn(
            'text-xs font-semibold truncate',
            expired
              ? 'text-status-error'
              : isSelected
                ? 'text-brand-primary'
                : 'text-text-secondary',
          )}
        >
          {name}
        </span>
      </div>
    </button>
  );
}

interface SelectedSponsorDetailProps {
  /** "TPA • PayerName • Policy" composite line. */
  description: string;
  expirationDate?: string | null;
  /** Insurance only — indicator tags above the description.
   *  Flutter `insurance_child_widget.dart:354–506` `selectedInsuranceView`. */
  indicators?: string[];
  onIndicatorClick?: () => void;
  /** Sponsor-aware expired text — "Insurance Expired" / "Corporate Expired"
   *  (Flutter insurance_child_widget.dart:478 / corporate_child_widget.dart:463).
   *  This component is shared by both sponsor types, so the caller supplies it.
   *  Falls back to the neutral label when null/empty/omitted. */
  expiredLabel?: string | null;
}

function SelectedSponsorDetail({
  description,
  expirationDate,
  indicators,
  onIndicatorClick,
  expiredLabel,
}: SelectedSponsorDetailProps) {
  const expired = isExpiredIso(expirationDate);
  return (
    <div className="rounded-xl border border-border-default p-4 mt-4">
      {indicators && indicators.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2 mb-2">
          {indicators.map((indicator) => (
            <button
              key={indicator}
              type="button"
              onClick={onIndicatorClick}
              className="inline-flex items-center gap-1 rounded border border-status-error px-2 py-0.5 text-xs font-semibold text-status-error hover:bg-red-50"
            >
              {indicator}
            </button>
          ))}
        </div>
      ) : null}
      <p className="text-sm text-text-primary leading-snug truncate">{description}</p>
      <p className="text-sm text-text-primary mt-3">
        <span>Exp Date: </span>
        {expirationDate ? (
          expired ? (
            <span className="text-status-error">
              {expiredLabel || SPONSOR_LABELS.expiredFallback}
            </span>
          ) : (
            <span>{formatExpDate(expirationDate)}</span>
          )
        ) : (
          <span>N/A</span>
        )}
      </p>
    </div>
  );
}

/** Indicators row for selected insurance — Flutter `insurance_child_widget.dart`. */
function insuranceIndicators(ins: HealthInsurance): string[] {
  const out: string[] = [];
  if (!ins.frontPhotoUrl || !ins.backPhotoUrl) out.push('Add Card Photos');
  if (isExpiredIso(ins.expirationDate)) out.push('Expired');
  if (!ins.isVerified) out.push('Verify Insurance');
  return out;
}

/**
 * Breakdown rows differ per tab — Flutter
 *   self_pay_child_widget.dart:52-129  → Gross / Discount / Net
 *   corporate_child_widget.dart:251-318 → Gross / Discount / Net / Patient Payable
 *   insurance_child_widget.dart:252-333 → Gross / Discount / Net / Patient Payable / Sponsor Amount Total
 */
type BreakdownVariant = 'self-pay' | 'insurance' | 'corporate';

function BreakdownCard({
  variant,
  gross,
  discount,
  net,
  patientPayable,
  sponsorOutstanding,
  currency,
}: {
  variant: BreakdownVariant;
  gross: number;
  discount: number;
  net: number;
  patientPayable: number;
  sponsorOutstanding: number;
  currency: string;
}) {
  const fmt = (v: number) => `${currency} ${v.toFixed(2)}`;
  const Row = ({ label, value }: { label: string; value: string }) => (
    <div className="flex items-center justify-between py-1">
      <span className="text-sm font-semibold text-text-primary">{label}</span>
      <span className="text-sm font-semibold text-text-primary">{value}</span>
    </div>
  );
  return (
    <div className="rounded-xl border border-border-default p-4 mt-4 space-y-1">
      <Row label="Gross Total" value={fmt(gross)} />
      <Row label="Discount Total" value={fmt(discount)} />
      <Row label="Net Total" value={fmt(net)} />
      {variant !== 'self-pay' ? (
        <Row label="Patient Payable" value={fmt(patientPayable)} />
      ) : null}
      {variant === 'insurance' ? (
        <Row label="Sponsor Amount Total" value={fmt(sponsorOutstanding)} />
      ) : null}
    </div>
  );
}

export function PaymentSection({
  practiceId: _practiceId,
  organizationId: _organizationId,
  currency = 'AED',
  onEditInsurance,
  onEditCorporate,
  onAddInsurance,
  onAddCorporate,
  editInvoice = null,
}: PaymentSectionProps) {
  const { watch, setValue, getValues, trigger } = useFormContext<AppointmentFormValues>();
  const sponsorType = watch('sponsorType');
  const providerId = watch('providerId');
  const patientId = watch('patientId');
  const location = watch('location');
  const billingProfileId = watch('billingProfileId');
  const sponsorId = watch('sponsorId');
  const policyNo = watch('policyNo');
  const invoiceTotals = watch('invoiceTotals');
  const healthInsurances = (watch('healthInsurances') ?? []) as HealthInsurance[];
  const corporateAgreements = (watch('corporateAgreements') ?? []) as CorporateAgreement[];
  const addedServices = watch('addedServices');

  const paymentType: 'Self-pay' | 'Insurance' | 'Corporate' =
    (sponsorType ?? 'Self-Pay') === 'Self-Pay'
      ? 'Self-pay'
      : (sponsorType as 'Insurance' | 'Corporate');
  // Memoized so the price re-calc only re-runs when an actual input changes,
  // not on every render (PR #474 review). React Query already dedups by hashed
  // queryKey, but memoizing also avoids rebuilding the individualServices array
  // each render. `addedServices` is a stable RHF watch reference between edits.
  const calculateParams = useMemo(
    () =>
      providerId && patientId && location?.id
        ? {
            providerId,
            patientId,
            locationId: location.id,
            billingProfileId,
            sponsorId,
            paymentType,
            // PROD-1025 (F-SCH-182/184): forward the user's selected services so
            // the pre-book price reflects them (previously hardcoded []). qty
            // defaults to 1 per Flutter IndividualService.toJson.
            individualServices: addedServicesToIndividualServices(addedServices),
          }
        : null,
    [
      providerId,
      patientId,
      location?.id,
      billingProfileId,
      sponsorId,
      paymentType,
      addedServices,
    ],
  );
  // Edit mode with a persisted invoice: the invoice IS the source of truth for
  // the TOTAL (Flutter `payment_widget.dart` reads `appointment.invoice.*`).
  // Disable the PreBook estimate here — it prices the form's `addedServices`,
  // which is decoupled from the invoice, so it showed stale totals (e.g. 950
  // vs the invoice's 400) after a billing-tab service edit (PROD-1809).
  const hasPersistedInvoice = !!editInvoice?.id;
  const calculatePrice = useCalculatePrice(hasPersistedInvoice ? null : calculateParams);

  useEffect(() => {
    if (calculatePrice.data) {
      // The warning is shown next to the breakdown, not stored with the totals.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { coPaymentWarning: _warning, ...totals } = calculatePrice.data;
      setValue('invoiceTotals', totals);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculatePrice.data]);

  // Flutter parity (`payment_widget.dart:307` — `PreLoadAppointmentState`
  // assigns `appointment.invoice = response.appointment.invoice`): keep the
  // TOTAL in lockstep with the persisted invoice. `editInvoice` is the live
  // PreLoadAppointmentDetails invoice, which `invalidateBalanceViews` refetches
  // after every invoice edit — so add/remove/qty on the billing tab now flows
  // straight through to this card with no reload. `sponsorOutstanding`
  // (the sponsor/insurance portion) maps to the invoice's `insuranceAmount`,
  // mirroring Flutter's `sponsorOutstanding ← amountResponse.sponsorAmount`.
  useEffect(() => {
    if (!hasPersistedInvoice || !editInvoice) return;
    setValue('invoiceTotals', {
      grossTotal: editInvoice.grossTotal ?? 0,
      discountTotal: editInvoice.discountTotal ?? 0,
      netTotal: editInvoice.netTotal ?? 0,
      patientPayable: editInvoice.patientPayable ?? 0,
      sponsorOutstanding: editInvoice.insuranceAmount ?? 0,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasPersistedInvoice,
    editInvoice?.grossTotal,
    editInvoice?.discountTotal,
    editInvoice?.netTotal,
    editInvoice?.patientPayable,
    editInvoice?.insuranceAmount,
  ]);

  // Select a sponsor when the tab changes or the payer list arrives —
  // Flutter `payment_widget.dart:151-195`.
  //
  // KEEP THE APPOINTMENT'S OWN PAYER IF IT IS IN THE LIST. Flutter does this
  // explicitly (:181-190): it takes `indexWhere(provider.id == appointment
  // .sponsor.id && policyNumber == appointment.policyNo)` and only falls back
  // to index 0 when the appointment has no sponsor of this type.
  //
  // This used to be an unconditional `[0]`, and because `healthInsurances` is
  // in the dep list the effect re-ran the moment the payer list loaded — after
  // `appointmentToFormValues` had already seeded the saved sponsor. So opening
  // any appointment whose payer was not first in the patient's list silently
  // reselected the first one. Reported 2026-08-22: patient's active payer is
  // Alliance, the panel kept showing the expired Orient.
  //
  // Not cosmetic — the reselected payer is what the next Save writes, so an
  // edit to a note or a time silently moved the appointment's payer AND policy
  // number to the wrong insurer. Measured on dev: sponsor went from index 1 to
  // index 0 (policy `13/XW/…` → `25D8-…`) on a save that never touched the
  // sponsor tab. When the wrongly-reselected payer is expired, the expired-
  // sponsor guard in `create-appointment-panel.tsx` then blocks the save
  // outright, so the appointment cannot be edited at all.
  //
  // Current selection is read via `getValues` rather than added to the deps:
  // the effect answers "is the selection still valid for this list?", and
  // re-running it on every pick would fight the user's own clicks.
  useEffect(() => {
    if (sponsorType === 'Insurance') {
      // Same predicate as the card + chip highlight below, so a preserved
      // selection is guaranteed to render as selected.
      const pick = pickSponsorSelection(
        healthInsurances.map((ins) => ({
          ...ins,
          providerId: ins.insuranceProvider?.id ?? null,
        })),
        getValues('sponsorId'),
        getValues('policyNo'),
      );
      if (pick?.insuranceProvider?.id) {
        setValue('sponsorId', pick.insuranceProvider.id);
        setValue('policyNo', pick.policyNumber ?? null);
        setValue('sponsor', {
          id: pick.insuranceProvider.id,
          payerName: pick.insuranceProvider.payerName,
          name: pick.insuranceProvider.name,
          imageUrl: pick.insuranceProvider.imageUrl,
          payerCompanyId: pick.insuranceProvider.payerCompanyId,
          relatedTpa: pick.insuranceProvider.relatedTpa,
        });
      } else {
        setValue('sponsorId', null);
        setValue('policyNo', null);
        setValue('sponsor', null);
      }
    } else if (sponsorType === 'Corporate') {
      const pick = pickSponsorSelection(
        corporateAgreements.map((corp) => ({
          ...corp,
          providerId: corp.corporateProvider?.id ?? null,
        })),
        getValues('sponsorId'),
        getValues('policyNo'),
      );
      if (pick?.corporateProvider?.id) {
        setValue('sponsorId', pick.corporateProvider.id);
        setValue('policyNo', pick.policyNumber ?? null);
        setValue('sponsor', {
          id: pick.corporateProvider.id,
          payerName: pick.corporateProvider.payerName,
          name: pick.corporateProvider.name,
          imageUrl: pick.corporateProvider.imageUrl,
          payerCompanyId: pick.corporateProvider.payerCompanyId,
          relatedTpa: pick.corporateProvider.relatedTpa,
        });
      } else {
        setValue('sponsorId', null);
        setValue('policyNo', null);
        setValue('sponsor', null);
      }
    } else {
      setValue('sponsorId', null);
      setValue('policyNo', null);
      setValue('sponsor', null);
    }
    // Re-run the schema for `sponsorId`. Its "agreement is required" error is
    // raised on `sponsorId` by the form's superRefine, and validating only
    // `sponsorType` (setTab) never re-evaluates it. Without this, switching
    // Insurance -> Self-Pay left the stale error behind and Save stayed
    // disabled until a reload (prod, appointment edited 2026-10-01).
    void trigger('sponsorId');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sponsorType, healthInsurances, corporateAgreements]);

  const selectInsurance = (item: HealthInsurance) => {
    if (!item.insuranceProvider?.id) return;
    setValue('sponsorId', item.insuranceProvider.id);
    setValue('policyNo', item.policyNumber ?? null);
    setValue('sponsor', {
      id: item.insuranceProvider.id,
      payerName: item.insuranceProvider.payerName,
      name: item.insuranceProvider.name,
      imageUrl: item.insuranceProvider.imageUrl,
      payerCompanyId: item.insuranceProvider.payerCompanyId,
      relatedTpa: item.insuranceProvider.relatedTpa,
    });
    void trigger('sponsorId');
  };

  const selectCorporate = (item: CorporateAgreement) => {
    if (!item.corporateProvider?.id) return;
    setValue('sponsorId', item.corporateProvider.id);
    setValue('policyNo', item.policyNumber ?? null);
    setValue('sponsor', {
      id: item.corporateProvider.id,
      payerName: item.corporateProvider.payerName,
      name: item.corporateProvider.name,
      imageUrl: item.corporateProvider.imageUrl,
      payerCompanyId: item.corporateProvider.payerCompanyId,
      relatedTpa: item.corporateProvider.relatedTpa,
    });
    void trigger('sponsorId');
  };

  // The tab being SHOWN can differ from the form's `sponsorType`, as in
  // Flutter `payment_widget.dart:173-193` (SetPaymentTypeState): picking
  // Insurance / Corporate for a patient with none on file shows that tab's
  // empty "add" view but does NOT change the appointment's payer, and clears
  // the sponsor. The payer only becomes Insurance / Corporate once there is a
  // record to select. React used to set `sponsorType` on the tap, which let a
  // patient with no insurance be saved as Insurance with no insurer
  // (prod, 2026-10-01).
  type PayerTab = 'Self-Pay' | 'Insurance' | 'Corporate';
  const [viewTab, setViewTab] = useState<PayerTab>(
    (sponsorType ?? 'Self-Pay') as PayerTab,
  );
  // Follow the form when the payer changes from outside this section
  // (loaded appointment, patient pick, a commit below).
  useEffect(() => {
    setViewTab((sponsorType ?? 'Self-Pay') as PayerTab);
  }, [sponsorType]);

  const hasRecordsFor = (tab: PayerTab) =>
    tab === 'Self-Pay' ||
    (tab === 'Insurance' && healthInsurances.length > 0) ||
    (tab === 'Corporate' && corporateAgreements.length > 0);

  const setTab = (next: PayerTab) => {
    setViewTab(next);
    if (hasRecordsFor(next)) {
      setValue('sponsorType', next, { shouldValidate: true });
      return;
    }
    // Flutter: `widget.appointment?.sponsor = null`, payer unchanged.
    setValue('sponsorId', null);
    setValue('policyNo', null);
    setValue('sponsor', null);
    void trigger('sponsorId');
  };

  // An insurance / corporate record added from the empty view: commit the
  // payer the user is looking at, as Flutter does once a record exists.
  useEffect(() => {
    if (viewTab !== (sponsorType ?? 'Self-Pay') && hasRecordsFor(viewTab)) {
      setValue('sponsorType', viewTab, { shouldValidate: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [healthInsurances, corporateAgreements]);

  // Description line (Flutter `insurance_child_widget.dart:377-378`)
  // = "{TPA companyName} • {payerName} • {policyNumber}". TPA can be
  // missing — the leading bullet drops with it.
  const selectedInsurance = healthInsurances.find(
    (ins) =>
      !!ins.insuranceProvider?.id &&
      ins.insuranceProvider.id === sponsorId &&
      (ins.policyNumber ?? '') === (policyNo ?? ''),
  );
  const selectedCorporate = corporateAgreements.find(
    (corp) =>
      !!corp.corporateProvider?.id &&
      corp.corporateProvider.id === sponsorId &&
      (corp.policyNumber ?? '') === (policyNo ?? ''),
  );

  // Edit mode: recalculate the breakdown when the user switches sponsor type
  // (Self-pay / Insurance / Corporate) — Flutter `payment_widget.dart` fires
  // `postBookCalculatePrice` from its SetPaymentType / SelectInsurance /
  // SelectCorporate handlers (there IS a persisted invoice, so it's the
  // *PostBook* endpoint, not PreBook). We only fire on a *consistent* change:
  //  - Insurance/Corporate require a picked payer that belongs to the current
  //    type, which guards the transient tick where `sponsorType` has flipped
  //    but the auto-select effect hasn't set the matching `sponsorId` yet.
  //  - The first render is skipped: the persisted invoice already reflects the
  //    saved type, so there's nothing to recompute on open.
  // The response updates the displayed totals only (like Flutter's in-memory
  // `appointment.invoice` write); Save commits + refetches every surface.
  const postBookCalc = usePostBookCalculatePrice();
  const lastSponsorSigRef = useRef<string | null>(null);
  useEffect(() => {
    const invoiceId = editInvoice?.id;
    // NOT gated on `providerId`. A device booking (PROD-2007) has no
    // practitioner, and "billing on arrival" is precisely the device flow, so
    // a provider gate here silently disabled the whole sponsor recalc for
    // devices. It was never load-bearing: the aggregator's
    // `PostBookCalculatePriceRequest` binds only { PatientId, SponsorId,
    // PaymentType, OrganizationId (from the header), InvoiceId } — there is no
    // ProviderId property, so the key the hook sends is dropped by the model
    // binder. The real preconditions are a persisted invoice (what we
    // recalculate) and a patient (co-payment lookup on Insurance/Corporate).
    if (!hasPersistedInvoice || !invoiceId || !patientId) return;

    const readySponsorId =
      paymentType === 'Self-pay'
        ? undefined
        : paymentType === 'Insurance'
          ? selectedInsurance?.insuranceProvider?.id
          : selectedCorporate?.corporateProvider?.id;
    // Insurance/Corporate: wait for a payer that matches the current type.
    if (paymentType !== 'Self-pay' && !readySponsorId) return;

    const sig = `${paymentType}|${readySponsorId ?? ''}`;
    if (lastSponsorSigRef.current === null) {
      // First settle — record the persisted type without recomputing.
      lastSponsorSigRef.current = sig;
      return;
    }
    if (lastSponsorSigRef.current === sig) return;
    lastSponsorSigRef.current = sig;

    postBookCalc.mutate(
      {
        patientId,
        invoiceId,
        // Guid.Empty for a device booking — the key is required by the
        // curl-pinned request shape but unbound server-side (see the gate
        // above), and Guid.Empty is what Billing stores on a device invoice.
        providerId: providerId || EMPTY_GUID,
        paymentType,
        ...(readySponsorId ? { sponsorId: readySponsorId } : {}),
        individualServices: [],
      },
      {
        onSuccess: (res) =>
          setValue('invoiceTotals', {
            grossTotal: res.grossTotal ?? 0,
            discountTotal: res.discountTotal ?? 0,
            netTotal: res.netTotal ?? 0,
            patientPayable: res.patientPayable ?? 0,
            // Flutter maps sponsorOutstanding ← response.sponsorAmount
            // (normalised by the hook).
            sponsorOutstanding: res.sponsorOutstanding ?? 0,
          }),
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasPersistedInvoice,
    editInvoice?.id,
    paymentType,
    selectedInsurance,
    selectedCorporate,
    patientId,
    providerId,
  ]);

  const insuranceDescription = (() => {
    if (!selectedInsurance) return '';
    const provider = selectedInsurance.insuranceProvider;
    const tpa = (provider as unknown as { relatedTpa?: { companyName?: string }[] })
      ?.relatedTpa;
    const tpaName = tpa && tpa.length > 0 ? tpa[0]?.companyName : null;
    const parts = [
      tpaName,
      payerName(provider),
      selectedInsurance.policyNumber,
    ].filter(Boolean);
    return parts.join(' • ');
  })();

  const corporateDescription = (() => {
    if (!selectedCorporate) return '';
    const provider = selectedCorporate.corporateProvider;
    const tpa = (provider as unknown as { relatedTpa?: { companyName?: string }[] })
      ?.relatedTpa;
    const tpaName = tpa && tpa.length > 0 ? tpa[0]?.companyName : null;
    const parts = [
      tpaName,
      payerName(provider),
      selectedCorporate.policyNumber,
    ].filter(Boolean);
    return parts.join(' • ');
  })();

  // PROD-3347: the price has no co-payment because the patient has no active
  // insurance for the appointment's insurer. From whichever call priced it.
  // While a new price loads, the query keeps the previous result; hide its
  // warning so an earlier insurer's name never shows for the new one.
  const coPaymentWarning = hasPersistedInvoice
    ? postBookCalc.data?.coPaymentWarning
    : calculatePrice.isPlaceholderData
      ? null
      : calculatePrice.data?.coPaymentWarning;

  const renderBreakdown = (variant: BreakdownVariant) =>
    calculatePrice.isPending && !invoiceTotals ? (
      <Skeleton className="h-32 w-full mt-4" />
    ) : (
      <BreakdownCard
        variant={variant}
        gross={invoiceTotals?.grossTotal ?? 0}
        discount={invoiceTotals?.discountTotal ?? 0}
        net={invoiceTotals?.netTotal ?? 0}
        patientPayable={invoiceTotals?.patientPayable ?? 0}
        sponsorOutstanding={invoiceTotals?.sponsorOutstanding ?? 0}
        currency={currency}
      />
    );

  return (
    <div className="px-6 py-4 border-t border-border-subtle">
      <label className="text-xs font-medium text-text-muted mb-3 block">
        Payment
      </label>

      <Segmented<string>
        options={SPONSOR_TYPE_OPTIONS}
        value={viewTab}
        onChange={(v) => setTab(v as typeof viewTab)}
        aria-label="Payment type"
      />

      {viewTab === 'Self-Pay' && (
        <>
          {/* Flutter `self_pay_child_widget.dart:56` shows a "TOTAL" sub
              header above the breakdown card — only on Self-pay. */}
          <div className="mt-4">
            <span className="text-xs font-medium text-text-muted">
              Total
            </span>
          </div>
          {renderBreakdown('self-pay')}
        </>
      )}

      {viewTab === 'Insurance' && (
        <>
          <div className="mt-4 flex items-center justify-between">
            <span className="text-xs font-medium text-text-muted">
              Insurance
            </span>
            {onEditInsurance ? (
              <button
                type="button"
                onClick={onEditInsurance}
                className="text-sm font-semibold text-brand-primary hover:text-interactive-hover"
              >
                Edit
              </button>
            ) : null}
          </div>
          {healthInsurances.length === 0 ? (
            <div className="mt-2 rounded-xl border border-border-default p-3 flex items-center justify-between gap-3">
              <p className="text-sm text-text-muted flex-1">
                No Insurance information has been added.
              </p>
              {onAddInsurance ? (
                <button
                  type="button"
                  onClick={onAddInsurance}
                  className="px-3 py-1 rounded text-sm font-semibold bg-grey-100 text-brand-primary hover:bg-grey-200"
                >
                  Add
                </button>
              ) : null}
            </div>
          ) : (
            <div className="mt-3 flex gap-3 overflow-x-auto scrollbar-hide pb-1">
              {healthInsurances.map((ins, i) => {
                const provider = ins.insuranceProvider;
                const name = payerName(provider);
                const selected =
                  !!provider?.id &&
                  provider.id === sponsorId &&
                  (ins.policyNumber ?? '') === (policyNo ?? '');
                return (
                  <SponsorChip
                    key={ins.healthInsuranceId ?? `ins-${i}`}
                    imageUrl={provider?.imageUrl}
                    name={name}
                    isSelected={selected}
                    isVerified={ins.isVerified}
                    isExpired={isExpiredIso(ins.expirationDate)}
                    onClick={() => selectInsurance(ins)}
                  />
                );
              })}
            </div>
          )}
          {selectedInsurance ? (
            <SelectedSponsorDetail
              description={insuranceDescription}
              expirationDate={selectedInsurance.expirationDate}
              indicators={insuranceIndicators(selectedInsurance)}
              onIndicatorClick={onEditInsurance}
              expiredLabel={SPONSOR_LABELS.insuranceExpired}
            />
          ) : null}
          <CoPaymentWarningBanner warning={coPaymentWarning} className="mt-4" />
          {renderBreakdown('insurance')}
        </>
      )}

      {viewTab === 'Corporate' && (
        <>
          <div className="mt-4 flex items-center justify-between">
            <span className="text-xs font-medium text-text-muted">
              Corporate
            </span>
            {onEditCorporate ? (
              <button
                type="button"
                onClick={onEditCorporate}
                className="text-sm font-semibold text-brand-primary hover:text-interactive-hover"
              >
                Edit
              </button>
            ) : null}
          </div>
          {corporateAgreements.length === 0 ? (
            <div className="mt-2 rounded-xl border border-border-default p-3 flex items-center justify-between gap-3">
              <p className="text-sm text-text-muted flex-1">
                No Corporate agreement information has been added.
              </p>
              {onAddCorporate ? (
                <button
                  type="button"
                  onClick={onAddCorporate}
                  className="px-3 py-1 rounded text-sm font-semibold bg-grey-100 text-brand-primary hover:bg-grey-200"
                >
                  Add
                </button>
              ) : null}
            </div>
          ) : (
            <div className="mt-3 flex gap-3 overflow-x-auto scrollbar-hide pb-1">
              {corporateAgreements.map((corp, i) => {
                const provider = corp.corporateProvider;
                const name = payerName(provider);
                const selected =
                  !!provider?.id &&
                  provider.id === sponsorId &&
                  (corp.policyNumber ?? '') === (policyNo ?? '');
                return (
                  <SponsorChip
                    key={corp.corporateAgreementId ?? `corp-${i}`}
                    imageUrl={provider?.imageUrl}
                    name={name}
                    isSelected={selected}
                    isVerified={corp.isVerified}
                    isExpired={isExpiredIso(corp.expirationDate)}
                    onClick={() => selectCorporate(corp)}
                  />
                );
              })}
            </div>
          )}
          {selectedCorporate ? (
            <SelectedSponsorDetail
              description={corporateDescription}
              expirationDate={selectedCorporate.expirationDate}
              expiredLabel={SPONSOR_LABELS.corporateExpired}
            />
          ) : null}
          {renderBreakdown('corporate')}
        </>
      )}
    </div>
  );
}
